<template>
	<div :style="{ display: 'flex', justifyContent: 'end', margin: '20px' }">
		<LanguageDropdown :languageList="languageList" :defaultLang="language"
			@language-changed="handleLanguageChange" />
	</div>

	<div class="styleDiv">
		<div v-if="loading" class="loader-container">
			<div class="simple-spinner"></div>
		</div>
		<div v-else>
			<a-form :model="formState" class="form">
				<div>
					<img src="../assets/logo.png" alt="Vue logo" class="image" />
					<div
						style="text-align: center; margin: 20px; font-size: 22px; color: #6d2144; font-weight: 600; font-family: sans-serif; letter-spacing: 0.3px">
						{{ translateText('del_ui_login_heading_text') }}</div>
				</div>
				<a-form-item class="formItem">
					<a-input class="input" v-model:value="formState.email"
						:placeholder="translateText('del_ui_input_email_text')" />
				</a-form-item>
				<a-form-item class="formItem" name="password">
					<a-input-password v-model:value="formState.password" class="input"
						:placeholder="translateText('del_ui_pass_input_text')" />
				</a-form-item>

				<a-button type="primary" size="large" class="btn" @click="onSubmit">{{
					translateText('del_ui_login_btn_text')
				}}</a-button>

				<div>
					<div class="mainDiv">
						<div class="subDiv">
							<span :style="{ margin: '0px 5px' }">{{ translateText('del_ui_signin_option_text') }}</span>
						</div>
					</div>
					<div class="socialMediaLogoContainer">
						<div>
							<button type="button" class="button_container">
								<GoogleLogin :callback="loginWithGoogle">
									<img src="../../public/google.png" alt="Login with google"
										style="width: 46px; height: 46px" />
								</GoogleLogin>
							</button>
						</div>
						<div>
							<button @click="loginWithFacebook" type="button" class="button_container">
								<img src="../../public/sm_facebook.png" alt="Login with Facebook"
									style="width: 46px; height: 46px" />
							</button>
						</div>
						<div>
							<button @click="loginWithApple" type="button" class="button_container">
								<img src="../../public/apple.png" alt="Login with Apple"
									style="width: 46px; height: 46px" />
							</button>
						</div>
					</div>
				</div>
			</a-form>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { loginApi, LoginWithAppleAPI, LoginWithGoogleAPI, loginWithFacebookAPI, getLanguageList } from "@/services/api/auth";
import { UnwrapRef, reactive, ref } from "vue";
import { openNotificationWithIcon } from "../utils/index";
import router from "@/router";
import { jwtDecode } from "jwt-decode";
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from "../.config";
import LanguageDropdown from "@/components/LanguageDropdown.vue";

interface FormState {
	email: string;
	password: string;
}
localStorage.removeItem("user");

const loading = ref(false);
const formState: UnwrapRef<FormState> = reactive({
	email: "",
	password: "",
});
const device_language = navigator.language.replace("-", "_");
const language = ref(device_language === "pt_BR" ? "PT" : "EN");
const languageList = ref<any[]>([]);
const translate = ref<any[]>([]);

const handleLanguageChange = async (lang: string) => {
	language.value = lang;
	localStorage.setItem('lang_for_delete', lang);
};

const fetchLanguages = async () => {
	try {
		loading.value = true;
		const key = '78 - Delete UI';
		const res = await getLanguageList(key);

		languageList.value = res.languageList.length
			? res.languageList.filter((lang: any) => lang.key === "EN" || lang.key === "PT")
			: [];
		translate.value = res.translate || [];

		const deletePageKeys = ["del_ui_del_btn_delete_account", "del_ui_del_page_intro_1", "del_ui_del_page_intro_2", "del_ui_del_page_intro_3",
			"del_ui_del_btn_cancel", "del_ui_del_page_heading", 'del_ui_del_model_info', 'del_ui_account_delete_msg',
			'del_ui_network_error', 'del_ui_user_not_exist', 'del_ui_support_team_msg', 'del_ui_del_model_del_btn'];

		const deletePageTranslations = translate.value.filter((item: any) =>
			deletePageKeys.includes(item.key)
		);

		localStorage.setItem('lang_for_delete', language.value);
		localStorage.setItem(
			"delete_page_translate",
			JSON.stringify({ deletePageTranslations, languageList: languageList.value, })
		);
		loading.value = false;
	} catch (err: any) {
		loading.value = false;
		console.error("Failed to fetch language list:", err.message || err);
	}
};

fetchLanguages();


const translateText = (key: string, params: Record<string, string> = {}): string => {
	if (!translate.value || !Array.isArray(translate.value)) return key;

	const translationItem = translate.value.find((item) => item.key === key);
	if (!translationItem) return key;

	const userLangLower = language.value?.toLowerCase();
	const selectedLang = languageList.value.find(
		(lang) => lang.key?.toLowerCase() === userLangLower
	);
	const selectedLangId = selectedLang?._id;

	let translation: any;

	if (selectedLangId) {
		translation = translationItem.translate.find(
			(t: any) => t.languageId === selectedLangId && t.isPublished
		)?.value;
	}

	if (!translation) {
		const enLang = languageList.value.find((lang) => lang.key?.toLowerCase() === "en");
		const enLangId = enLang?._id;
		if (enLangId) {
			translation = translationItem.translate.find(
				(t: any) => t.languageId === enLangId && t.isPublished
			)?.value;
		}
	}

	if (!translation) translation = translationItem.base;

	if (translation && params) {
		Object.keys(params).forEach((k) => {
			const regex = new RegExp(`%this\\.user\\.${k}%`, "g");
			translation = translation.replace(regex, params[k]);
		});
	}

	return translation;
};

const onSubmit = async () => {
	try {
		const trimmedEmail = formState.email.trim();

		if (!trimmedEmail) {
			return openNotificationWithIcon("warning", translateText('del_ui_login_email_validation_text'));
		}

		// Validate email format using a simple regex pattern
		const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		if (!emailPattern.test(trimmedEmail)) {
			return openNotificationWithIcon("warning", translateText("del_ui_email_validate"));
		}

		// Update formState.email with the trimmed value
		formState.email = trimmedEmail;

		// Trim password before validation
		const trimmedPassword = formState.password.trim();

		if (!trimmedPassword) {
			return openNotificationWithIcon("warning", translateText('del_ui_login_password_validation_text'));
		}

		// Password should have a minimum length (e.g., 6 characters)
		if (trimmedPassword.length < 6) {
			return openNotificationWithIcon("warning", translateText('del_ui_password_validate'));
		}

		// Update formState.password with the trimmed value
		formState.password = trimmedPassword;

		const apiRes: any = await loginApi(formState);
		if (!apiRes) {
			return openNotificationWithIcon("error", translateText('del_ui_invalid_email_pass_text'));
		}

		if (apiRes.msg?.includes("You logged in via")) {
			const msg = apiRes.msg;
			if (/facebook/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_facebook_social_account_text"));
			} else if (/google/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_google_social_account_text"));
			} else if (/apple/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_apple_social_account_text"));
			}
			return openNotificationWithIcon("warning", msg);
		}

		if (apiRes.msg && !apiRes.valid) {
			if (apiRes.key === 'not-found') {
				return openNotificationWithIcon("warning", translateText('del_ui_user_not_found'));
			}
			else if (apiRes.msg?.includes("Invalid password")) {
				return openNotificationWithIcon("warning", translateText('del_ui_invalid_pass_text'));
			}
			else {
				return openNotificationWithIcon("warning", apiRes.msg);
			}
		}

		if (!apiRes.roles || !apiRes.roles.includes("active")) {
			return openNotificationWithIcon("warning", translateText('del_ui_access_msg'));
		}

		apiRes.adminLoginTime = new Date().getTime();
		localStorage.setItem("user", JSON.stringify(apiRes));
		openNotificationWithIcon("success", translateText('del_ui_login_success_text'));
		router.push("/delete-account");
	} catch (error: any) {
		if (!error?.response && error?.message === "Network Error") {
			return openNotificationWithIcon("error", translateText("del_ui_network_error"));
		}

		const serverResponse = error?.response?.data;
		const msg = serverResponse?.msg || error?.response?.data?.msg;

		if (msg?.includes("You logged in via")) {
			if (/facebook/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_facebook_social_account_text"));
			}
			if (/google/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_google_social_account_text"));
			}
			if (/apple/i.test(msg)) {
				return openNotificationWithIcon("warning", translateText("del_ui_apple_social_account_text"));
			}
			return openNotificationWithIcon("warning", msg);
		}

		return openNotificationWithIcon("error", translateText("del_ui_server_response_text"));
	}

};

const loginWithFacebook = () => {
	window.FB.login(
		(response: any) => {
			if (response.authResponse) {
				window.FB.api("/me", { fields: "name,email" }, async (response: any) => {
					try {
						const apiRes: any = await loginWithFacebookAPI({
							identifier: response.email,
							socialId: response.id,
						});

						if (apiRes && apiRes.success && apiRes.user) {
							localStorage.setItem("user", JSON.stringify(apiRes.user));
							openNotificationWithIcon("success", translateText('del_ui_login_success_text'));
							router.push("/delete-account");
						}
					} catch (error: any) {
						console.error("Error in login:", error);
					}
				});
			}
		},
		{ scope: "public_profile,email" }
	);
};

const loginWithGoogle = async (response: any) => {
	try {
		const code = response.code;
		if (!code) {
			console.error("No authorization code returned");
			return;
		}
		const currentUrl = window.location.origin;
		const apiRes: any = await LoginWithGoogleAPI({
			code: code,
			baseurl: currentUrl,
		});
		if (apiRes && apiRes.success && apiRes.user) {
			localStorage.setItem("user", JSON.stringify(apiRes.user));
			openNotificationWithIcon("success", translateText('del_ui_login_success_text'));
			router.push("/delete-account");
		} else {
			openNotificationWithIcon("error", translateText('del_ui_user_not_found'));
		}
	} catch (error) {
		console.error("Error during Google login:", error);
		openNotificationWithIcon("error", translateText('del_ui_error_msg_text'));
	}
};

const loginWithApple = async () => {
	try {
		window.AppleID.auth.init({
			clientId: APPLE_CLIENT_ID,
			scope: "name email",
			redirectURI: APPLE_REDIRECT_URI,
			usePopup: true,
		});
		const response = await window.AppleID.auth.signIn();
		const {
			authorization: { id_token },
		} = response;

		const decoded: any = jwtDecode(id_token);
		const email = decoded.email;
		const socialId = decoded.sub;
		const apiRes = await LoginWithAppleAPI({
			identifier: email,
			socialId: socialId,
		});

		if (apiRes && apiRes.success && apiRes.user) {
			localStorage.setItem("user", JSON.stringify(apiRes.user));
			openNotificationWithIcon("success", translateText('del_ui_login_success_text'));
			router.push("/delete-account");
		} else {
			openNotificationWithIcon("error", translateText('del_ui_login_failed'));
		}
	} catch (error) {
		console.error("Apple Login Error", error);
	}
};
</script>

<style scoped>
.styleDiv {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.form {
	background-color: #fff;
	width: 360px;
	padding: 3rem;
	box-shadow: 0 10px 25px rgba(92, 99, 105, 0.50);
	border-radius: 15px;

	.ant-form-item-label {
		position: relative;
		/* height: 30px; */
	}
}

.input {
	text-align: left;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 37px;
	border: 1px solid #dadce0;
	outline: none;
	padding: 1rem;
	background: none;
	z-index: 1;
}

.image {
	width: 70%;
	height: auto;
	margin: auto;
	display: block;
}

.formItem {
	position: relative;
}

.btn {
	width: 100%;
	letter-spacing: 0.3px;
}

.zm-login-methods__title {
	position: relative;
	text-align: center;
	margin-top: 12px;
}

.button_container {
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	margin-bottom: 20px;
}

.zm-login-methods__title span {
	background-color: #fff;
	padding-left: 12px;
	padding-right: 12px;
	display: inline-block;
	line-height: 24px;
	position: relative;
	z-index: 1;
	color: #747487;
}

.mainDiv {
	border-top: 1px solid rgb(190, 190, 190);
	position: relative;
	margin: 25px 0px;
	display: flex;
	justify-content: center;
	align-items: center;
}

.subDiv {
	display: flex;
	position: absolute;
	top: -11px;
	background: #fff;
	margin-left: auto;
	margin-right: auto;
	padding: 0 5px;
	font-size: 15px;
	justify-content: center;
	align-items: center;
	color: rgb(190, 190, 190);
}

.socialMediaLogoContainer {
	display: flex;
	gap: 10px;
	justify-content: center;
}

.loader-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 200px;
}

.simple-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #ccc;
	border-top-color: #44142a;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
